<template>
  <section class="card engagement-page">
    <header class="head">
      <div>
        <h2>会员活跃度</h2>
        <p class="tip">统计选定时间内至少有一项活跃行为的用户（含已注册会员与未注册参会人）；图表为主、列表可导出。个人明细请在「详情」中查看。</p>
      </div>
      <div class="head-actions">
        <button type="button" class="btn btn-edit" :disabled="loading || !total" @click="exportCsv">导出 CSV</button>
      </div>
    </header>

    <div class="filters filters-panel">
      <div class="flt">
        <FancySelect v-model="rangePreset" class="flt-select" :options="rangePresetOptions" placeholder="时间范围" />
      </div>
      <div class="flt flt--range">
        <div class="range-row">
          <input v-model="startDate" class="flt-input flt-input--time" type="date" aria-label="开始日期" />
          <span class="range-sep">~</span>
          <input v-model="endDate" class="flt-input flt-input--time" type="date" aria-label="结束日期" />
        </div>
      </div>
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="姓名 / 手机 / 单位"
          @keyup.enter="reload"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="statusCode" class="flt-select" :options="statusOptions" placeholder="全部状态" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" :disabled="loading" @click="reload">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" :disabled="loading" @click="resetFilters">重置</button>
      </div>
    </div>

    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="stats-grid overview-cards">
      <div v-for="card in overviewCards" :key="card.key" class="stat-card accent-blue">
        <div class="stat-label">{{ card.label }}</div>
        <div class="stat-value">{{ card.value }}</div>
      </div>
    </div>

    <div class="chart-grid">
      <div class="chart-card chart-card--wide">
        <div class="chart-title">活跃趋势（去重用户数 / 行为次数）</div>
        <div ref="trendChartRef" class="chart-box"></div>
      </div>
      <div class="chart-card">
        <div class="chart-title">行为构成</div>
        <div ref="compositionChartRef" class="chart-box"></div>
      </div>
    </div>

    <div class="chart-grid chart-grid--single">
      <div class="chart-card">
        <div class="chart-title">活跃 Top10（行为次数）</div>
        <div ref="topChartRef" class="chart-box chart-box--tall"></div>
      </div>
      <div class="chart-card">
        <div class="chart-title">各维度次数对比</div>
        <div ref="dimensionChartRef" class="chart-box chart-box--tall"></div>
      </div>
    </div>

    <h3 class="section-title">活跃用户列表</h3>
    <p v-if="loading" class="muted empty-hint">加载中…</p>
    <template v-else>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th class="col-seq">序号</th>
              <th>姓名</th>
              <th>手机</th>
              <th>单位</th>
              <th>身份</th>
              <th class="col-num sortable" @click="toggleSort('signup')">报名</th>
              <th class="col-num sortable" @click="toggleSort('checkin')">签到</th>
              <th class="col-num sortable" @click="toggleSort('view')">浏览</th>
              <th class="col-num sortable" @click="toggleSort('survey')">问卷</th>
              <th class="col-num sortable" @click="toggleSort('total')">合计</th>
              <th class="sortable" @click="toggleSort('lastActive')">最近活跃</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in pageRows" :key="rowKey(row, idx)">
              <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
              <td>{{ row.registerName || '—' }}</td>
              <td>{{ row.registerMobile || '—' }}</td>
              <td class="ellipsis" :title="row.registerCompanyName">{{ row.registerCompanyName || '—' }}</td>
              <td>{{ identityLabel(row) }}</td>
              <td class="col-num">{{ row.signupCount }}</td>
              <td class="col-num">{{ row.checkinCount }}</td>
              <td class="col-num col-view-breakdown">
                <div v-for="line in viewBreakdownLines(row)" :key="line" class="view-breakdown-line">{{ line }}</div>
              </td>
              <td class="col-num">{{ row.surveyCount }}</td>
              <td class="col-num"><strong>{{ row.totalBehaviorCount }}</strong></td>
              <td>{{ row.lastActiveAt || '—' }}</td>
              <td>
                <button type="button" class="btn btn-edit btn-sm" @click="openDetail(row)">详情</button>
              </td>
            </tr>
            <tr v-if="!pageRows.length && !errorMsg">
              <td colspan="12" class="table-empty muted">暂无活跃记录</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPager
        v-model:page="page"
        v-model:page-size="pageSize"
        :total="total"
      />
    </template>

    <MemberEngagementDetailDialog
      :visible="detailOpen"
      :member-id="detailMemberId"
      :guest-phone="detailGuestPhone"
      :range-params="rangeParams"
      @close="detailOpen = false"
    />
  </section>
</template>

<script>
import * as echarts from 'echarts'
import ListPager from '@/components/ListPager.vue'
import FancySelect from '@/components/FancySelect.vue'
import MemberEngagementDetailDialog from '@/components/member/MemberEngagementDetailDialog.vue'
import {
  getAdminEngagementOverview,
  getAdminEngagementTrend,
  getAdminEngagementComposition,
  getAdminMemberEngagementSummary,
} from '@/api'
import {
  ENGAGEMENT_CHART_COLORS,
  engagementGrid,
  exportEngagementCsv,
  formatRangeParams,
  engagementIdentityLabel,
  engagementViewBreakdownLines,
  toDateInputValue,
} from '@/utils/engagementCharts.js'

export default {
  name: 'MemberEngagementManageView',
  components: { ListPager, FancySelect, MemberEngagementDetailDialog },
  data() {
    const end = new Date()
    const start = new Date(end)
    start.setDate(start.getDate() - 89)
    return {
      loading: false,
      errorMsg: '',
      rangePreset: '90',
      startDate: toDateInputValue(start),
      endDate: toDateInputValue(end),
      keyword: '',
      statusCode: '',
      page: 1,
      pageSize: 20,
      sort: 'lastActive',
      order: 'desc',
      overview: null,
      trend: null,
      composition: null,
      allRows: [],
      topListRows: [],
      total: 0,
      trendChart: null,
      compositionChart: null,
      topChart: null,
      dimensionChart: null,
      detailOpen: false,
      detailMemberId: null,
      detailGuestPhone: '',
      rangePresetOptions: [
        { value: '30', label: '近 30 天' },
        { value: '90', label: '近 90 天' },
        { value: '365', label: '近 1 年' },
        { value: 'custom', label: '自定义' },
      ],
      statusOptions: [
        { value: '', label: '全部状态' },
        { value: 'ACTIVE', label: '正式会员' },
        { value: 'REGISTERED', label: '已注册' },
      ],
    }
  },
  computed: {
    rangeParams() {
      return formatRangeParams(this.rangePreset, this.startDate, this.endDate)
    },
    overviewCards() {
      const o = this.overview || {}
      return [
        { key: 'active', label: '活跃用户数', value: o.activeUserCount ?? 0 },
        { key: 'total', label: '行为总次数', value: o.totalBehaviorCount ?? 0 },
        { key: 'avg', label: '人均行为', value: o.avgBehaviorPerActiveUser ?? 0 },
        { key: 'signup', label: '报名次数', value: o.signupEventCount ?? 0 },
        { key: 'checkin', label: '签到人次', value: o.checkinEventCount ?? 0 },
        { key: 'viewUsers', label: '浏览人数', value: o.activityViewUserCount ?? 0 },
      ]
    },
    pageRows() {
      return this.allRows
    },
    rowOffset() {
      return (this.page - 1) * this.pageSize
    },
    topRows() {
      return this.topListRows
    },
  },
  watch: {
    page() {
      this.loadSummary()
    },
    pageSize() {
      this.page = 1
      this.loadSummary()
    },
    statusCode() {
      this.page = 1
      this.reload()
    },
    rangePreset(val) {
      if (val !== 'custom') {
        const p = formatRangeParams(val, '', '')
        this.startDate = p.start.slice(0, 10)
        this.endDate = p.end.slice(0, 10)
        this.page = 1
        this.reload()
      }
    },
  },
  mounted() {
    this.initCharts()
    this.reload()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    identityLabel(row) {
      return engagementIdentityLabel(row)
    },
    viewBreakdownLines(row) {
      return engagementViewBreakdownLines(row)
    },
    initCharts() {
      if (this.$refs.trendChartRef) this.trendChart = echarts.init(this.$refs.trendChartRef)
      if (this.$refs.compositionChartRef) this.compositionChart = echarts.init(this.$refs.compositionChartRef)
      if (this.$refs.topChartRef) this.topChart = echarts.init(this.$refs.topChartRef)
      if (this.$refs.dimensionChartRef) this.dimensionChart = echarts.init(this.$refs.dimensionChartRef)
      if (this.topChart) {
        this.topChart.on('click', (params) => {
          const row = this.resolveTopChartRow(params)
          if (row) this.openDetail(row)
        })
      }
    },
    disposeCharts() {
      [this.trendChart, this.compositionChart, this.topChart, this.dimensionChart].forEach((c) => {
        if (c) c.dispose()
      })
    },
    handleResize() {
      [this.trendChart, this.compositionChart, this.topChart, this.dimensionChart].forEach((c) => {
        if (c) c.resize()
      })
    },
    async reload() {
      this.page = 1
      this.loading = true
      this.errorMsg = ''
      try {
        await Promise.all([this.loadOverviewBundle(), this.loadSummary(), this.loadTopList()])
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async loadOverviewBundle() {
      const params = this.rangeParams
      const [overview, trend, composition] = await Promise.all([
        getAdminEngagementOverview(params),
        getAdminEngagementTrend({ ...params, granularity: 'day' }),
        getAdminEngagementComposition(params),
      ])
      this.overview = overview || {}
      this.trend = trend || { points: [] }
      this.composition = composition || { slices: [] }
      this.$nextTick(() => this.renderCharts())
    },
    async loadSummary() {
      const params = {
        ...this.rangeParams,
        keyword: this.keyword,
        status: this.statusCode,
        page: this.page,
        pageSize: this.pageSize,
        sort: this.sort,
        order: this.order,
      }
      const data = await getAdminMemberEngagementSummary(params)
      this.allRows = data?.rows || []
      this.total = data?.total ?? 0
    },
    async loadTopList() {
      const data = await getAdminMemberEngagementSummary({
        ...this.rangeParams,
        keyword: this.keyword,
        status: this.statusCode,
        page: 1,
        pageSize: 10,
        sort: 'total',
        order: 'desc',
      })
      this.topListRows = data?.rows || []
      this.$nextTick(() => this.renderTopCharts())
    },
    topRowLabel(row) {
      const name = String(row?.registerName || '').trim()
      const mobile = String(row?.registerMobile || '').trim()
      if (name) return name
      if (mobile) return mobile
      if (row?.registered === false) return mobile || '未注册'
      return `#${row?.memberId || ''}`
    },
    rowKey(row, idx) {
      if (row?.registered === false || !Number(row?.memberId)) {
        return `guest-${row?.registerMobile || idx}`
      }
      return `m-${row.memberId}`
    },
    resolveTopChartRow(params) {
      const list = this.topRows
      if (!list.length || !params) return null
      const name = String(params.name || '').trim()
      if (name) {
        const hit = list.find((r) => this.topRowLabel(r) === name)
        if (hit) return hit
      }
      const idx = Number(params.dataIndex)
      if (!Number.isFinite(idx) || idx < 0 || idx >= list.length) return null
      // 横向条形图 data 与 y 轴均 reverse，需反算索引
      return list[list.length - 1 - idx] || list[idx]
    },
    renderCharts() {
      const points = this.trend?.points || []
      if (this.trendChart) {
        this.trendChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'axis' },
          legend: { data: ['活跃用户数', '行为次数'], bottom: 0, textStyle: { color: '#667085' } },
          grid: { ...engagementGrid(), bottom: 48 },
          xAxis: {
            type: 'category',
            data: points.map((p) => p.label),
            axisLabel: { color: '#667085' },
          },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f7' } } },
          series: [
            { name: '活跃用户数', type: 'line', smooth: true, data: points.map((p) => p.activeUserCount) },
            { name: '行为次数', type: 'bar', barWidth: 12, data: points.map((p) => p.behaviorCount) },
          ],
        })
      }
      const slices = this.composition?.slices || []
      if (this.compositionChart) {
        this.compositionChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'item' },
          legend: { bottom: 0, icon: 'circle', textStyle: { color: '#667085' } },
          series: [{
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '44%'],
            data: slices.map((s) => ({ name: s.name, value: s.value })),
            label: { formatter: '{b}\n{c}' },
          }],
        })
      }
      this.renderTopCharts()
    },
    renderTopCharts() {
      const top = this.topRows
      if (this.topChart) {
        this.topChart.setOption({
          color: ['#2f6af6'],
          tooltip: { trigger: 'axis' },
          grid: { left: 96, right: 24, top: 16, bottom: 24 },
          xAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f7' } } },
          yAxis: {
            type: 'category',
            data: top.map((r) => this.topRowLabel(r)).reverse(),
            axisLabel: { color: '#667085', width: 80, overflow: 'truncate' },
          },
          series: [{
            type: 'bar',
            data: top.map((r) => r.totalBehaviorCount).reverse(),
            barWidth: 16,
            itemStyle: { borderRadius: [0, 8, 8, 0] },
          }],
        })
      }
      const o = this.overview || {}
      if (this.dimensionChart) {
        this.dimensionChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'axis' },
          grid: engagementGrid(),
          xAxis: {
            type: 'category',
            data: ['报名', '签到', '浏览', '问卷'],
            axisLabel: { color: '#667085' },
          },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f7' } } },
          series: [{
            type: 'bar',
            barWidth: 28,
            data: [
              o.signupEventCount || 0,
              o.checkinEventCount || 0,
              o.activityViewEventCount || 0,
              o.surveySubmitCount || 0,
            ],
            itemStyle: { borderRadius: [8, 8, 0, 0] },
          }],
        })
      }
    },
    toggleSort(key) {
      if (this.sort === key) {
        this.order = this.order === 'desc' ? 'asc' : 'desc'
      } else {
        this.sort = key
        this.order = 'desc'
      }
      this.page = 1
      this.loadSummary().catch((e) => {
        this.errorMsg = e.message || '排序失败'
      })
    },
    resetFilters() {
      this.rangePreset = '90'
      const p = formatRangeParams('90', '', '')
      this.startDate = p.start.slice(0, 10)
      this.endDate = p.end.slice(0, 10)
      this.keyword = ''
      this.statusCode = ''
      this.reload()
    },
    exportCsv() {
      getAdminMemberEngagementSummary({
        ...this.rangeParams,
        keyword: this.keyword,
        status: this.statusCode,
        page: 1,
        pageSize: Math.max(this.total, 5000),
        sort: this.sort,
        order: this.order,
      })
        .then((data) => exportEngagementCsv(data?.rows || []))
        .catch((e) => {
          this.errorMsg = e.message || '导出失败'
        })
    },
    openDetail(row) {
      const registered = row?.registered !== false && Number(row?.memberId) > 0
      if (registered) {
        this.detailMemberId = row.memberId
        this.detailGuestPhone = ''
      } else {
        this.detailMemberId = 0
        this.detailGuestPhone = String(row?.registerMobile || '').trim()
      }
      this.detailOpen = true
    },
  },
}
</script>

<style scoped>
.engagement-page .tip { color: #667085; margin: 4px 0 0; }
.overview-cards { margin-top: 12px; }
.stats-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}
.stat-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e6edf8;
  border-radius: 12px;
  padding: 14px;
}
.stat-label { color: #2a4b8d; font-size: 12px; }
.stat-value { font-size: 22px; font-weight: 800; margin-top: 6px; color: #0b2a5b; }
.col-view-breakdown { min-width: 88px; text-align: left; vertical-align: middle; }
.view-breakdown-line { font-size: 12px; line-height: 1.45; color: #344054; white-space: nowrap; }
.chart-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 12px;
  margin-top: 14px;
}
.chart-grid--single { grid-template-columns: 1fr 1fr; }
.chart-card {
  background: #fff;
  border: 1px solid #e9eef5;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}
.chart-card--wide { grid-column: span 1; }
.chart-title { font-size: 14px; font-weight: 700; color: #1d2939; margin-bottom: 8px; }
.chart-box { width: 100%; height: 300px; }
.chart-box--tall { height: 340px; }
.section-title { margin: 18px 0 10px; font-size: 15px; color: #344054; }
.sortable { cursor: pointer; user-select: none; }
.col-num { text-align: center; width: 64px; }
@media (max-width: 1200px) {
  .stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .chart-grid, .chart-grid--single { grid-template-columns: 1fr; }
}
</style>
